"use client";

import "@/src/styles/index.css";
import React, { useEffect, useState } from "react";
import Link from "next/link";
import { useAuth0 } from "@auth0/auth0-react";
import { trackEvent } from "@/src/helpers/trackEvent";
import { SettingsSheets } from "@/src/components/Settings/SettingsSheet";
import { Sheet, SheetContent, SheetTrigger } from "@/src/components/ui/sheet";
import { Menu } from "lucide-react";
import { QuarkleLogo, QuarkleWordmark } from "@/src/components/Brand";
import { formatMasthead } from "@/src/components/Home/LandingV6/ui";

/* Top-of-page navbar used on /, /pro, /pricing, /blog, and inside the
 * authenticated Studio. Visually it matches the landing-v6 Nav (dashed
 * masthead strip + horizontal logo lockup + Gambarino italic links) but uses
 * the --qk-* design variables so it sits naturally inside the Studio shell.
 */
const LINKS = [
  { href: "/features", label: "Features" },
  { href: "/pro", label: "Pro" },
  { href: "/pricing", label: "Pricing" },
  { href: "/blog", label: "Stories" },
];

function NavLink({ href, label, external, accent, title }) {
  const common = {
    title,
    style: {
      color: accent ? "var(--qk-pink-deep)" : "var(--qk-ink)",
      textDecoration: "none",
      fontFamily: "'Gambarino', 'DM Serif Display', serif",
      fontStyle: "italic",
      fontSize: accent ? 17 : 15,
      letterSpacing: accent ? "0.02em" : "-0.01em",
      opacity: accent ? 1 : 0.85,
      fontWeight: accent ? 500 : undefined,
      transition: "opacity 0.15s",
      display: "inline-flex",
      alignItems: "center",
      gap: 7,
    },
  };
  const content = accent ? (
    <>
      <span
        aria-hidden
        style={{
          width: 7,
          height: 7,
          borderRadius: 999,
          background: "var(--qk-pink-deep)",
          animation: "qk-qpulse 1.4s infinite",
        }}
      />
      {label}
    </>
  ) : (
    label
  );
  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...common}>
        {content}
      </a>
    );
  }
  return (
    <Link href={href} {...common}>
      {content}
    </Link>
  );
}

/* Dateline — one dashed strip with a dynamically computed vol/issue/date
 * string. Same format as the landing-v6 Nav. */
function Dateline() {
  const [mast, setMast] = useState(() => formatMasthead());
  useEffect(() => {
    setMast(formatMasthead());
    const t = setInterval(() => setMast(formatMasthead()), 60_000);
    return () => clearInterval(t);
  }, []);
  return (
    <div
      className="qv6-nav-masthead hidden items-center justify-between px-10 py-1 lg:flex"
      style={{
        borderBottom: "1px dashed var(--qk-rule)",
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 10,
        color: "var(--qk-muted)",
        letterSpacing: "0.02em",
      }}
    >
      <span>{mast.left}</span>
      <span>{mast.right}</span>
    </div>
  );
}

function DiscordIconLink() {
  return (
    <a
      href="https://discord.gg/cPfBDmZNM2"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Join our Discord"
      title="Join our Discord"
      className="inline-flex items-center justify-center transition-colors"
      style={{
        width: 36,
        height: 36,
        borderRadius: 999,
        border: "1px solid var(--qk-ink)",
        color: "var(--qk-ink)",
        background: "transparent",
        textDecoration: "none",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "var(--qk-ink)";
        e.currentTarget.style.color = "var(--qk-paper)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.color = "var(--qk-ink)";
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M20.317 4.369a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.249a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.249.077.077 0 0 0-.079-.037 19.736 19.736 0 0 0-4.885 1.515.07.07 0 0 0-.032.027C.533 9.046-.32 13.579.099 18.057a.082.082 0 0 0 .031.056 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.873-1.295 1.226-1.994a.076.076 0 0 0-.041-.105 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.927 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.009c.12.099.246.198.372.292a.077.077 0 0 1-.006.128 12.3 12.3 0 0 1-1.873.891.077.077 0 0 0-.041.106c.36.699.772 1.364 1.226 1.993a.076.076 0 0 0 .084.029 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.055c.5-5.177-.838-9.673-3.549-13.66a.061.061 0 0 0-.031-.028zM8.02 15.331c-1.182 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.211 0 2.177 1.095 2.157 2.419 0 1.334-.955 2.419-2.157 2.419zm7.975 0c-1.182 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.211 0 2.176 1.095 2.157 2.419 0 1.334-.946 2.419-2.157 2.419z" />
      </svg>
    </a>
  );
}

function LoginPill() {
  const { loginWithRedirect } = useAuth0();
  return (
    <button
      type="button"
      className="qk-pill hidden lg:inline-flex"
      onClick={() => {
        trackEvent("Sign Up");
        loginWithRedirect();
      }}
    >
      log in
    </button>
  );
}

function StartWritingPill() {
  const { loginWithRedirect } = useAuth0();
  return (
    <button
      type="button"
      className="qk-pill flex items-center gap-1.5"
      style={{
        background: "var(--qk-pink-deep)",
        color: "var(--qk-on-pink)",
        borderColor: "var(--qk-pink-deep)",
        boxShadow: "3px 3px 0 var(--qk-ink)",
      }}
      onClick={() => {
        trackEvent("Sign Up", { "Signup Type": "Nav" });
        loginWithRedirect({ screen_hint: "signup" });
      }}
      onMouseEnter={(e) => (e.currentTarget.style.transform = "translate(-2px,-2px)")}
      onMouseLeave={(e) => (e.currentTarget.style.transform = "translate(0,0)")}
    >
      start writing
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--qk-lime)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M13 5l7 7-7 7" />
      </svg>
    </button>
  );
}

function MobileNavItem({ href, label, external, accent, onClick }) {
  const style = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    borderRadius: 12,
    padding: "12px 16px",
    color: accent ? "var(--qk-pink-deep)" : "var(--qk-ink)",
    textDecoration: "none",
  };
  const content = (
    <>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "'Gambarino', serif", fontStyle: "italic", fontSize: 18 }}>
        {accent && (
          <span
            aria-hidden
            style={{
              width: 7,
              height: 7,
              borderRadius: 999,
              background: "var(--qk-pink-deep)",
              animation: "qk-qpulse 1.4s infinite",
            }}
          />
        )}
        {label}
      </span>
      <span className="qk-mono" style={{ fontSize: 10, color: "var(--qk-muted)", letterSpacing: "0.14em" }}>→</span>
    </>
  );
  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" style={style} onClick={onClick}>
        {content}
      </a>
    );
  }
  return (
    <Link href={href} style={style} onClick={onClick}>
      {content}
    </Link>
  );
}

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const { isAuthenticated } = useAuth0();

  useEffect(() => {
    const handler = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <header
      className="qk-editor fixed left-0 right-0 top-0 z-50 w-full transition-all"
      style={{
        background: isScrolled
          ? "color-mix(in oklab, var(--qk-paper) 82%, transparent)"
          : "var(--qk-paper)",
        backdropFilter: isScrolled ? "blur(14px)" : "none",
        borderBottom: "1px solid var(--qk-ink)",
        color: "var(--qk-ink)",
      }}
    >
      <Dateline />

      <nav
        className="qv6-nav-row grid items-center gap-6 px-6 py-3 lg:px-10 lg:py-[14px]"
        style={{ gridTemplateColumns: "auto 1fr auto" }}
      >
        {/* Left: logo lockup — same horizontal treatment as landing. */}
        <div className="flex items-center gap-3">
          {/* Mobile hamburger */}
          <Sheet>
            <SheetTrigger asChild>
              <button
                type="button"
                className="flex h-9 w-9 items-center justify-center rounded-full md:hidden"
                style={{ border: "1px solid var(--qk-rule)", color: "var(--qk-ink)" }}
                aria-label="open menu"
              >
                <Menu className="h-4 w-4" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[320px] p-0" selected={null}>
              <div className="flex flex-col">
                <div className="px-6 py-5" style={{ borderBottom: "1px dashed var(--qk-rule)" }}>
                  <Link href="/" className="flex items-center gap-3" style={{ color: "var(--qk-ink)", textDecoration: "none" }}>
                    <QuarkleLogo size={36} />
                    <QuarkleWordmark size={28} />
                  </Link>
                  <div
                    className="mt-2 qk-mono"
                    style={{ fontSize: 10, color: "var(--qk-muted)", letterSpacing: "0.18em" }}
                  >
                    / a writing co.
                  </div>
                </div>
                <nav className="flex-1 p-3">
                  {isAuthenticated && <MobileNavItem href="/editor/0" label="Write" />}
                  {LINKS.filter((l) => !l.accent).map((l) => (
                    <MobileNavItem
                      key={l.href}
                      href={l.href}
                      label={l.label}
                      external={l.external}
                      accent={l.accent}
                    />
                  ))}
                  {isAuthenticated && (
                    <>
                      <MobileNavItem href="https://docs.quarkle.ai/" label="Docs" external />
                      <MobileNavItem href="/changelog" label="What's new" />
                    </>
                  )}
                  {LINKS.filter((l) => l.accent).map((l) => (
                    <MobileNavItem
                      key={l.href}
                      href={l.href}
                      label={l.label}
                      external={l.external}
                      accent={l.accent}
                    />
                  ))}
                  <MobileNavItem href="https://discord.gg/cPfBDmZNM2" label="Discord" external />
                </nav>
              </div>
            </SheetContent>
          </Sheet>

          <Link
            href="/"
            className="qv6-nav-logo flex items-center gap-3"
            style={{ color: "var(--qk-ink)", textDecoration: "none" }}
          >
            <QuarkleLogo size={isScrolled ? 36 : 42} />
            <span className="qv6-wordmark inline-flex items-center" style={{ lineHeight: 0.85 }}>
              <QuarkleWordmark size={isScrolled ? 28 : 34} />
            </span>
            {/* Decorative; below lg the link row needs the width more. */}
            <span
              className="qv6-nav-logo-sub qk-mono hidden lg:inline"
              style={{
                fontSize: 10,
                color: "var(--qk-muted)",
                letterSpacing: "0.18em",
                marginLeft: 4,
              }}
            >
              / a writing co.
            </span>
          </Link>
        </div>

        {/* Center: nav links */}
        <nav className="qv6-nav-links hidden items-center justify-center gap-5 md:flex lg:gap-7">
          {LINKS.filter((l) => !l.accent).map((l) => (
            <NavLink
              key={l.href}
              href={l.href}
              label={l.label}
              external={l.external}
              accent={l.accent}
              title={l.title}
            />
          ))}
          {isAuthenticated && (
            <>
              <NavLink href="https://docs.quarkle.ai/" label="Docs" external />
              <NavLink href="/changelog" label="What's new" />
            </>
          )}
          {LINKS.filter((l) => l.accent).map((l) => (
            <NavLink
              key={l.href}
              href={l.href}
              label={l.label}
              external={l.external}
              accent={l.accent}
              title={l.title}
            />
          ))}
        </nav>

        {/* Right: CTA / account */}
        <div className="flex items-center gap-2">
          <span className="hidden md:inline-flex">
            <DiscordIconLink />
          </span>
          {!isAuthenticated ? (
            <>
              <LoginPill />
              <StartWritingPill />
            </>
          ) : (
            <SettingsSheets />
          )}
        </div>
      </nav>
    </header>
  );
}
